<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useTranslation } from 'i18next-vue';
import { useAccessControlStore } from '@peanuds/app-shell';

import { useAccountSelection } from '@peanuds/shared-state';
import { MoneyTransactionDetailsDialog } from '@peanuds/money';
import {
  type TransactionDetailsDialogDirection,
  type TransactionDetailsDialogSelection,
  type TransactionDetailsDialogSource,
} from '@peanuds/modules';

import ControlSpendingsTable from './components/ControlSpendingsTable.vue';
import type { ControlSpendingsTableFilter } from './internal/control-spendings-table.types';

const { t } = useTranslation();
const searchQuery = ref('');

const tableFilters = computed(() => [
  { name: t('controlSpending.filters.all_transactions'), value: 'all-transactions' },
  { name: t('controlSpending.filters.incoming'), value: 'incoming' },
  { name: t('controlSpending.filters.outgoing'), value: 'outgoing' },
]);

const selectedFilterValue = ref<ControlSpendingsTableFilter>('all-transactions');

const { resolvedTransactionAccountIdsKey } = useAccountSelection();
const accessControlStore = useAccessControlStore();
const { isOnboarded } = storeToRefs(accessControlStore);

const isTransactionDetailsVisible = ref(false);
const selectedTransactionId = ref<string | null>(null);
const selectedTransactionDirection = ref<TransactionDetailsDialogDirection | null>(null);
const selectedTransactionSource = ref<TransactionDetailsDialogSource | null>(null);

const handleTransactionClick = ({
  transactionId,
  direction,
  transactionSource,
}: TransactionDetailsDialogSelection) => {
  selectedTransactionId.value = transactionId;
  selectedTransactionDirection.value = direction;
  selectedTransactionSource.value = transactionSource;
  isTransactionDetailsVisible.value = true;
};

const handleTransactionDetailsClose = () => {
  isTransactionDetailsVisible.value = false;
  selectedTransactionId.value = null;
  selectedTransactionDirection.value = null;
  selectedTransactionSource.value = null;
};

watch(
  () => [isOnboarded.value, resolvedTransactionAccountIdsKey.value] as const,
  ([onboarded, accountIdsKey], previousValue: readonly [boolean, string] | undefined) => {
    const [previousOnboarded, previousAccountIdsKey] = previousValue ?? [false, ''];

    if (!onboarded || accountIdsKey.length === 0) {
      handleTransactionDetailsClose();
      return;
    }

    if (onboarded === previousOnboarded && accountIdsKey === previousAccountIdsKey) return;

    handleTransactionDetailsClose();
  },
  { immediate: true },
);
</script>

<template>
  <div class="control-spendings" data-testid="dashboard-control-spendings">
    <div class="control-spendings__body">
      <div class="control-spendings__top">
        <h2 class="control-spendings__title">{{ $t('controlSpending.title') }}</h2>
      </div>
      <div class="control-spendings__header">
        <SelectButton
          class="control-spendings__filters"
          optionLabel="name"
          optionValue="value"
          v-model="selectedFilterValue"
          :options="tableFilters"
          :allowEmpty="false"
        />

        <IconField>
          <InputIcon class="pi pi-search"></InputIcon>
          <InputText
            :placeholder="$t('controlSpending.search_placeholder')"
            v-model="searchQuery"
            :pt="{
              root: { style: { borderRadius: 'var(--p-border-radius-xxl)' } },
            }"
          />
        </IconField>
      </div>
      <div class="control-spendings__table">
        <ControlSpendingsTable
          :search="searchQuery"
          :filter="selectedFilterValue"
          @transaction-click="handleTransactionClick"
        />
      </div>
    </div>

    <MoneyTransactionDetailsDialog
      v-model:visible="isTransactionDetailsVisible"
      :transaction-id="selectedTransactionId"
      :direction="selectedTransactionDirection"
      :transaction-source="selectedTransactionSource"
      @close="handleTransactionDetailsClose"
    />
  </div>
</template>

<style lang="scss" scoped>
.control-spendings {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex-direction: column;

  &__top {
    display: flex;
    justify-content: space-between;
    margin-bottom: $static-space-3; // 12px
    align-items: center;
    height: 38px; // Specific to design
  }

  &__title {
    color: var(--p-text-color);
    font-size: $static-text-md; // 18px
    font-weight: $font-regular;
  }

  &__button {
    align-self: flex-end;
  }

  &__body {
    display: flex;
    min-height: 0;
    flex-direction: column;
    flex: 1;
    background-color: white;
    padding: $static-space-4 $static-space-4 0; // 16px 16px 0
    border: 1px solid #cbd5e1;
    border-radius: var(--p-border-radius-xl);
  }

  &__header {
    padding-bottom: $static-space-4; // 16px
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: $static-space-2; // 8px

    :deep(.p-iconfield .p-inputtext:not(:first-child)) {
      padding-inline-start: 2.25rem;
    }

    :deep(.p-togglebutton-label) {
      font-size: $static-text-sm; // 14px
    }

    :deep(.p-iconfield .p-inputicon) {
      font-size: $static-text-sm; // 14px
    }

    :deep(.p-iconfield .p-inputtext) {
      font-size: $static-text-sm; // 14px
    }
  }

  &__filters {
    font-size: $static-text-sm; // 14px
  }

  &__table {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: visible;
  }
}
</style>
